import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./sidebarAddDocument.css";
import { documentsService } from "../../api/documentsService/documentsService";
import { toast } from "react-toastify";
import Button from "../ui/button/Button";
import { IDocument } from "../../types/documents";
import InputText from "../ui/inputText/InputText";
import { canPass } from "../../utils/helpers/validations/canPass";
import dayjs from "dayjs";
import { base64ToBlob } from "../../utils/helpers/base64ToBlob";
import { useUserFlowTracking } from "../../utils/hooks/useUserFlowTracking";
import SuccessPopup from "../ui/popup/SuccessPopup";

interface ISidebarProps {
  isOpen?: boolean;
  callbackCloseSidebar: () => void;
  activeDocument: IDocument | null;
  callbackUpdateDocuments: (documents: any) => void;
}

const SidebarAddDocument: React.FC<ISidebarProps> = ({
  isOpen = false,
  callbackCloseSidebar,
  activeDocument = null,
  callbackUpdateDocuments,
}) => {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<null | HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [metadataState, setMetadataState] = useState<
    { type: "Date" | "String"; fieldCRM: string; value: string }[]
  >([]);
  const [metadataErrorState, setMetadataErrorState] = useState<any>({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    setSelectedFile(null);
    setMetadataErrorState({});

    if (!!activeDocument && !!activeDocument.metadata) {
      const metas = [...activeDocument?.metadata].map((meta) => {
        return {
          fieldCRM: meta.fieldCRM,
          type: meta.type,
          value: "",
        };
      });
      setMetadataState(metas);
    }
  }, [activeDocument]);

  const handleDrag = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (
        !!e.dataTransfer.files[0].type &&
        (e.dataTransfer.files[0].type === "image/png" ||
          e.dataTransfer.files[0].type === "image/jpg" ||
          e.dataTransfer.files[0].type === "image/jpeg" ||
          e.dataTransfer.files[0].type === "application/pdf" ||
          e.dataTransfer.files[0].type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
      ) {
        setSelectedFile(e.dataTransfer.files[0]);
        setMetadataErrorState({
          ...metadataErrorState,
          file: false,
        });

        // Aggiorna il valore del nome del file in metadataState
        const copyMetadata = [...metadataState];
        const indexToModify = copyMetadata.findIndex(
          (m) => m.fieldCRM === "arcese_documentname"
        );
        if (indexToModify !== -1) {
          copyMetadata[indexToModify].value = e.dataTransfer.files[0].name;
          setMetadataState(copyMetadata);
        }
      } else {
        toast.error(t("errors.notSupportedDocument"));
      }
    }
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const handleChange = (e: any) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      if (
        !!e.target.files[0].type &&
        (e.target.files[0].type === "image/jpg" ||
          e.target.files[0].type === "image/jpeg" ||
          e.target.files[0].type === "image/png" ||
          e.target.files[0].type === "application/pdf" ||
          e.target.files[0].type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
      ) {
        setSelectedFile(e.target.files[0]);
        setMetadataErrorState({
          ...metadataErrorState,
          file: false,
        });

        // Update 'arcese_documentname' in metadataState
        const copyMetadata = [...metadataState];
        const indexToModify = copyMetadata.findIndex(
          (m) => m.fieldCRM === "arcese_documentname"
        );
        if (indexToModify !== -1) {
          copyMetadata[indexToModify].value = e.target.files[0].name;
          setMetadataState(copyMetadata);
        }
      } else {
        toast.error(t("errors.notSupportedDocument"));
      }
    }
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    // Clear 'arcese_documentname' in metadataState
    const copyMetadata = [...metadataState];
    const indexToModify = copyMetadata.findIndex(
      (m) => m.fieldCRM === "arcese_documentname"
    );
    if (indexToModify !== -1) {
      copyMetadata[indexToModify].value = "";
      setMetadataState(copyMetadata);
    }
  };

  const handleUpload = async () => {
    const errorObj = validateMetadataState();
    if (canPass(errorObj)) {
      if (
        !!activeDocument &&
        !!selectedFile &&
        !!selectedFile.type &&
        (selectedFile.type === "image/jpg" ||
          selectedFile.type === "image/jpeg" ||
          selectedFile.type === "image/png" ||
          selectedFile.type === "application/pdf" ||
          selectedFile.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
      ) {
        setLoading(true);
        try {
          const fileBase64: string = await getBase64(selectedFile);
          const base64WithoutPrefix = fileBase64.substring(
            ("data:" + selectedFile.type.toString() + ";base64,").length
          );

          // Map dates in metadata state!
          const copyMetadata = [...metadataState];
          const metas = copyMetadata.map((m) => {
            if (m.type === "Date") {
              return {
                ...m,
                value: dayjs(m.value).format("DD/MM/YYYY"),
              };
            }

            return m;
          });

          const data = {
            id:
              activeDocument.subDocuments?.length > 0
                ? activeDocument.subDocuments.find(
                  (doc) => doc.statusCode === 605530007
                )?.id || activeDocument.subDocuments[0].id
                : activeDocument.id,
            fileName: selectedFile.name,
            file: base64WithoutPrefix,
            metadata: metas,
          };

          const response = await documentsService.postAttachment(data);
          if (response.success) {
            // Refresh dei documenti e chiudo modale
            const responseGetDocuments = await documentsService.getDocuments();
            if (responseGetDocuments.success) {
              callbackUpdateDocuments(responseGetDocuments.responseBody);
              callbackCloseSidebar();

              // Mostra popup di successo se presente il messaggio
              if (
                activeDocument.popupMessage &&
                activeDocument.popupMessage.trim() !== ""
              ) {
                setShowSuccessPopup(true);
                setSuccessMessage(activeDocument.popupMessage);
              }
            } else {
              window.location.reload();
            }
          } else {
            console.error("error during upload of document");
            toast.error(t("errors.errorUploadingDocument"));
          }
        } catch (error: any) {
          console.error("error during elaboration of document");
          toast.error(t("errors.errorElaboratingDocument"));
          return;
        }
        setLoading(false);
      } else {
        toast.error(t("errors.notSupportedDocument"));
      }
    } else {
      setMetadataErrorState(errorObj);
    }
  };

  const validateMetadataState = () => {
    const errorObj: any = {};
    metadataState.forEach((m) => {
      if (!m.value && m.fieldCRM !== "arcese_documentname") {
        errorObj[m.fieldCRM] = {
          error: true,
        };
      }
    });

    if (!selectedFile) {
      errorObj["file"] = {
        error: true,
      };
    }

    return errorObj;
  };

  const getBase64: (file: any) => any = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleChangeMetadata = (
    e: any,
    type: "String" | "Date",
    field: string
  ) => {
    const copyMetadata = [...metadataState];
    const indexToModify = copyMetadata.findIndex((m) => m.fieldCRM === field);

    if (indexToModify !== -1) {
      copyMetadata[indexToModify].value = e;
      setMetadataState(copyMetadata);
    }
  };

  const getMetaValue = (field: string) => {
    if (!!metadataState && metadataState.length > 0) {
      const meta = metadataState.find((m) => m.fieldCRM === field)?.value;
      return !!meta ? meta : "";
    }

    return "";
  };

  const onButtonClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const resetError = (field: string) => () => {
    setMetadataErrorState({
      ...metadataErrorState,
      [field]: {
        errror: false,
      },
    });
  };

  const handleOpenFilePreview = async () => {
    if (!!selectedFile) {
      let docType: null | "png" | "jpg" | "docx" | "pdf" = null;
      if (selectedFile.name.includes(".pdf")) {
        docType = "pdf";
      } else if (selectedFile.name.includes(".docx")) {
        docType = "docx";
      } else if (selectedFile.name.includes(".png")) {
        docType = "png";
      } else if (selectedFile.name.includes(".jpg")) {
        docType = "jpg";
      }

      const fileBase64: string = await getBase64(selectedFile);
      const base64WithoutPrefix = fileBase64.substring(
        ("data:" + selectedFile.type.toString() + ";base64,").length
      );
      const blobObj = base64ToBlob(base64WithoutPrefix, docType);
      if (blobObj.success && blobObj.file !== null) {
        const url = URL.createObjectURL(blobObj.file);
        window.open(url, "_blank");
      }
    }
  };

  return (
    <>
      <SuccessPopup
        isOpen={showSuccessPopup}
        message={successMessage}
        onClose={() => setShowSuccessPopup(false)}
      />

      <aside
        className={
          "flex flex-col transition z-[999] fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar" +
          (isOpen === true
            ? " translate-x-0 sidebar-shadow"
            : " translate-x-[100%]")
        }
      >
        <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
          <div className="text-lg font-bold leading-[21.6px]">
            {t("common.sidebars.addDocument.heading")}
          </div>
          <div onClick={callbackCloseSidebar} className="cursor-pointer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-8 h-8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        </div>

        {/* MAIN CONTENT OF SIDEBAR */}
        <div className="grow overflow-auto px-[30px] py-8">
          {activeDocument !== null && (
            <>
              <div className="text-sm font-semibold text-red uppercase mb-2 leading-[17px]">
                {activeDocument.documentType}
              </div>
              <div className="text-black font-bold text-[22px] mb-10">
                {activeDocument.name}
              </div>

              {selectedFile === null && (
                <>
                  <form
                    id="form-file-upload"
                    onDragEnter={handleDrag}
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      id="input-file-upload"
                      multiple={true}
                      onChange={handleChange}
                    />
                    <label
                      id="label-file-upload"
                      htmlFor="input-file-upload"
                      className={dragActive ? "drag-active" : ""}
                    >
                      <div className="">
                        <div className="flex justify-center mb-[14px]">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            className="w-10 h-10 stroke-[#6F6F6F]"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                            />
                          </svg>
                        </div>
                        <button
                          className="upload-button"
                          onClick={onButtonClick}
                        >
                          {t("common.sidebars.addDocument.dragNDropText")}
                        </button>
                      </div>
                    </label>
                    {dragActive && (
                      <div
                        id="drag-file-element"
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                      ></div>
                    )}
                  </form>

                  {!!metadataErrorState["file"] &&
                    metadataErrorState["file"].error && (
                      <div className="text-xs text-red font-semibold mt-2">
                        {t("errors.requiredDocument")}
                      </div>
                    )}
                </>
              )}

              {!!selectedFile && (
                <>
                  <div className="font-semibold text-sm text-red uppercase leading-[17px] block mb-[5px]">
                    {t("common.sidebars.addDocument.document")}
                  </div>
                  <div className="flex items-center">
                    <div
                      className="mr-2 hover:underline cursor-pointer"
                      onClick={handleOpenFilePreview}
                    >
                      {selectedFile.name}
                    </div>
                    <div
                      onClick={handleDeleteFile}
                      className="cursor-pointer"
                      title="Delete"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 7H5V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V7H6ZM10 19H8V10H10V19ZM16 19H14V10H16V19ZM16.618 4L15 2H9L7.382 4H3V6H21V4H16.618Z"
                          className="fill-red"
                        />
                      </svg>
                    </div>
                  </div>
                </>
              )}

              {activeDocument &&
                !!activeDocument.metadata &&
                activeDocument.metadata.length > 0 && (
                  <div className="mt-5 grid grid-cols-12 gap-4">
                    {activeDocument.metadata.map((meta, index) => {
                      return (
                        <div key={index} className="col-span-12 lg:col-span-6">
                          <InputText
                            type={meta.type === "String" ? "text" : "date"}
                            name={meta.fieldCRM}
                            label={meta.label}
                            placeholder={meta.label}
                            value={getMetaValue(meta.fieldCRM)}
                            callbackOnChange={(e) =>
                              handleChangeMetadata(e, meta.type, meta.fieldCRM)
                            }
                            isRequired={meta.fieldCRM !== "arcese_documentname"}
                            hasError={
                              !!metadataErrorState[meta.fieldCRM] &&
                              metadataErrorState[meta.fieldCRM].error
                            }
                            errorMessage={t("errors.genericErrorRequired", {
                              label: meta.label.toLowerCase(),
                            }).toString()}
                            callbackOnFocus={resetError(meta.fieldCRM)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
            </>
          )}
        </div>

        <div className="shrink-0 py-6 px-[30px] text-right border-t">
          <Button callback={handleUpload} loading={loading}>
            {t("common.sidebars.saveButtonText")}
          </Button>
        </div>
      </aside>
    </>
  );
};

export default SidebarAddDocument;
